import { useEffect } from "react";
import seo from "../seo/routes.json";

/**
 * Per-route document metadata, client side.
 *
 * The build also bakes these same values into a static HTML file per route
 * (scripts/prerender-seo.js), so crawlers get them without running JS. This
 * hook keeps them correct across client-side navigation, where no new document
 * is ever fetched.
 *
 * It mutates the existing head tags rather than rendering new ones. React 19
 * can hoist <title>/<meta> from components, but it APPENDS them next to the
 * ones already in the document - leaving two descriptions and two canonicals,
 * with no guarantee which one Google reads.
 */
const setAttr = (selector, attr, value) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export const seoFor = (path) =>
  seo.routes.find((route) => route.path === path) || null;

export const useRouteSeo = (path) => {
  const route = seoFor(path);
  const title = route && route.title;
  const description = route && route.description;
  const url = route ? `${seo.origin}${route.canonical || route.path}` : null;

  useEffect(() => {
    if (!title || !description || !url) return;

    document.title = title;
    setAttr('meta[name="description"]', "content", description);
    setAttr('link[rel="canonical"]', "href", url);
    setAttr('meta[property="og:url"]', "content", url);
    setAttr('meta[property="og:title"]', "content", title);
    setAttr('meta[property="og:description"]', "content", description);
    setAttr('meta[name="twitter:title"]', "content", title);
    setAttr('meta[name="twitter:description"]', "content", description);
  }, [title, description, url]);
};
